import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { departementCourant } from '../services/droits';
import { stageAPI } from '../services/api';
import StageModale from './StageModale';
import { telechargerCsv } from '../services/csvExcel';

/**
 * SUIVI DE STAGE — l'onglet « Évaluation stagiaire » (lot 3.6 bis, revu au lot 3.5c-3)
 * ====================================================================================
 * Spec     : doc/modules/suivi_de_stage.md § 4 — Évaluation stagiaire, écarts E120 → E124, E145
 * Maquette : #tab-evaluations — recherche, filtre « Tous les processus », « Exporter » ; carte « Résultats
 * d'évaluation » ; colonnes Étudiant · Processus · Tuteur pédagogique · Évaluation maître de stage · Visite
 * tuteur ; une ligne grisée tant que rien n'est reçu ; le clic ouvre le VOLET DE DROITE (#evalVoletOverlay) :
 * ⑨ évaluation du maître de stage, ⑧ fiche de visite du tuteur, bouton « Dossier » (qui ouvre le Dossier étudiant, E163).
 *
 * ── CONSULTATION SEULEMENT ─────────────────────────────────────────────────
 * Aucun geste d'écriture ici : l'envoi, la relance et le renvoi vivent dans le panneau du dossier ;
 * la fiche de visite se remplit par le tuteur (lien ou « Mes visites »). Un profil en lecture seule voit
 * et exporte comme les autres.
 *
 * ── ÉCARTS VALIDÉS ─────────────────────────────────────────────────────────
 *   · le barème est sur CHAQUE note ; l'en-tête ne le porte que s'il est le même pour toutes les lignes
 *     visibles (E121) ; le détail dit le niveau choisi, les commentaires et le signataire (E122) ;
 *   · l'export reprend les lignes FILTRÉES, en CSV lisible par Excel en français (E124) ;
 *   · E145 (lot 3.5c-3) : volet de droite au lieu de l'accordéon, « Visite tuteur » au lieu de « Grille
 *     pédagogique », filtre par processus.
 */

const CLE_EXPORT = 'stage.evaluations.export';   // « Le choix est mémorisé pour la session »

const normaliser = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const nombre = (n) => (n === null || n === undefined ? '' : String(Number(n)).replace('.', ','));
const dateFr = (iso) => {
    if (!iso) return '';
    const d = /^\d{4}-\d{2}-\d{2}$/.test(String(iso)) ? new Date(iso + 'T12:00:00') : new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};
const jourMois = (iso) => dateFr(iso).slice(0, 5);
const heureFr = (v) => (v ? new Date(v).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');
const duree = (m) => (!m ? null : (Math.floor(m / 60) ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m} min`));
const tuteurTexte = (l) => (!l.visite_active ? 'Sans objet' : (l.tuteur || ''));
const ICONE_MODE = { presentiel: 'fa-building', telephonique: 'fa-phone', visio: 'fa-video' };

// Lot C.4b : la cellule et le téléchargement (« ; », BOM, échappement — E124) sont communs avec la Certification.

/**
 * Notes finales : la colonne de la note porte le NOM DE LA GRILLE, sans colonne « Barème » (recette du
 * 2026-10-01) ; une colonne par grille quand les campagnes en ont plusieurs. Lot 3.5c-3 : la visite du
 * tuteur suit (note, date, mode) — « Grille 1, Grille 2 » de la spec.
 */
function csvNotes(lignes) {
    const grilles = [...new Set(lignes.filter((l) => l.resultat).map((l) => l.resultat.grille_nom))];
    const colonnes = grilles.length ? grilles : ['Grille Entreprise'];
    return [['Nom', 'Prénom', 'N° étudiant', 'Formation', 'Semestre', 'Tuteur pédagogique', ...colonnes, 'Date de réception',
             'Visite tuteur', 'Visite tuteur (sur)', 'Date de visite', 'Mode de visite'],
        ...lignes.map((l) => [l.nom, l.prenom, l.code_etudiant, l.formation_nom, l.semestre, tuteurTexte(l),
            ...colonnes.map((g) => (l.resultat?.grille_nom === g ? nombre(l.resultat.total) : '')),
            dateFr(l.resultat?.date_soumission),
            l.visite ? nombre(l.visite.total) : '', l.visite ? nombre(l.visite.total_max) : '',
            dateFr(l.visite?.date_visite), l.visite?.mode_libelle || ''])];
}

function csvDetail(lignes) {
    const sortie = [['Nom', 'Prénom', 'N° étudiant', 'Formation', 'Semestre', 'Évaluation', 'Grille', 'Critère', 'Niveau', 'Points', 'Points max', 'Commentaire']];
    for (const l of lignes) {
        for (const c of l.resultat?.criteres || []) {
            sortie.push([l.nom, l.prenom, l.code_etudiant, l.formation_nom, l.semestre, 'Maître de stage', l.resultat.grille_nom,
                c.libelle, c.niveau, nombre(c.points), nombre(c.points_max), c.commentaire || '']);
        }
        for (const c of l.visite?.criteres || []) {
            sortie.push([l.nom, l.prenom, l.code_etudiant, l.formation_nom, l.semestre, 'Visite du tuteur', l.visite.modele_nom,
                c.libelle, c.niveau, nombre(c.points), nombre(c.points_max), c.commentaire || '']);
        }
    }
    return sortie;
}

/** La note du maître, ou ce qui en tient lieu. */
function CelluleNote({ ligne }) {
    if (!ligne.grille_configuree) return <span className="eval-attente">— Non configurée</span>;
    if (!ligne.resultat) return <span className="eval-attente">En attente</span>;
    return <span className="eval-note-badge">{nombre(ligne.resultat.total)} <small>/ {nombre(ligne.resultat.total_max)}</small></span>;
}

/** La visite du tuteur (maquette : note bleue, « · 12/05 · présentiel »), ou ce qui en tient lieu. */
function CelluleVisite({ ligne: l }) {
    if (!l.visite_active) return <span className="eval-attente">Sans objet</span>;
    if (l.visite) {
        return (
            <>
                <span className="eval-note-badge eval-note-visite">{nombre(l.visite.total)} <small>/ {nombre(l.visite.total_max)}</small></span>
                <span className="eval-visite-meta"> · {jourMois(l.visite.date_visite)} · {String(l.visite.mode_libelle || '').replace('En présentiel', 'présentiel').toLowerCase()}</span>
            </>
        );
    }
    if (!l.tuteur) return <span className="eval-attente">—</span>;
    if (l.echeance_visite) return <span className="eval-a-faire">À effectuer avant le {jourMois(l.echeance_visite)}</span>;
    return <span className="eval-attente">En attente</span>;
}

/** Une ligne de critère du volet (maquette `.ev-crit`) : libellé, niveau, points, commentaire. */
function LigneCritere({ c }) {
    return (
        <div className="ev-crit">
            <span>{c.libelle}</span>
            {c.niveau ? <span className="ev-niv">{c.niveau}</span> : <span />}
            <strong>{nombre(c.points)}{c.points_max !== null && c.points_max !== undefined ? ` / ${nombre(c.points_max)}` : ''}</strong>
            {c.commentaire && <div className="ev-com">« {c.commentaire} »</div>}
        </div>
    );
}

/** Le VOLET DE DROITE (E145) — ⑨ maître de stage, ⑧ fiche de visite. Le même vocabulaire que le panneau du dossier. */
function VoletEvaluation({ l, onFermer, onDossier }) {
    useEffect(() => {
        const touche = (e) => { if (e.key === 'Escape') onFermer(); };
        window.addEventListener('keydown', touche);
        return () => window.removeEventListener('keydown', touche);
    }, [onFermer]);
    const r = l.resultat, v = l.visite;
    const sous = [l.processus, l.entreprise_nom, l.date_debut && `${dateFr(l.date_debut)} → ${dateFr(l.date_fin)}`].filter(Boolean).join(' · ');
    return (
        <div className="stg-panneau-fond" onClick={onFermer}>
            <div className="stg-panneau certif-panneau eval-volet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Évaluation de ${l.nom} ${l.prenom}`}>
                <div className="stg-panneau-corps">
                    <div className="stg-panneau-entete">
                        <i className="fa-solid fa-star-half-stroke" style={{ color: 'var(--stg-vert-d)' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h3>{l.nom} {l.prenom}</h3>
                            <div className="stg-panneau-sous">{sous}</div>
                        </div>
                        {onDossier && (
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => onDossier(l)} title="Ouvrir le dossier étudiant">
                                <i className="fa-solid fa-folder-open" /> Dossier
                            </button>
                        )}
                        <button type="button" className="action-btn" onClick={onFermer} title="Fermer (Échap)"><i className="fa-solid fa-xmark" /></button>
                    </div>

                    {/* ⑨ Évaluation du maître de stage */}
                    <div className="detail-section">
                        <div className="detail-section-title">
                            <span><i className="fa-solid fa-briefcase" /> ⑨ Évaluation du maître de stage</span>
                            {r && <span className="eval-note-badge">{nombre(r.total)} / {nombre(r.total_max)}</span>}
                        </div>
                        {!l.grille_configuree ? <div className="eval-volet-vide">Pas d'évaluation par le maître de stage pour cette formation.</div>
                            : !r ? <div className="eval-volet-vide">Grille pas encore reçue.</div> : (
                                <>
                                    <div className="eval-volet-meta">Grille « {r.grille_nom} » · reçue le {dateFr(r.date_soumission)} · signée par {r.signature_nom}</div>
                                    {r.criteres.map((c, i) => <LigneCritere key={i} c={c} />)}
                                    {r.commentaire && <div className="ev-com ev-com-general"><b>Commentaire général :</b> « {r.commentaire} »</div>}
                                </>
                            )}
                    </div>

                    {/* ⑧ Fiche de visite du tuteur */}
                    <div className="detail-section">
                        <div className="detail-section-title">
                            <span><i className="fa-solid fa-chalkboard-user" /> ⑧ Fiche de visite du tuteur</span>
                            {v && <span className="eval-note-badge eval-note-visite">{nombre(v.total)} / {nombre(v.total_max)}</span>}
                        </div>
                        {!l.visite_active ? <div className="eval-volet-vide">Sans objet : pas de visite pour cette formation.</div>
                            : !v ? (
                                <div className="eval-volet-vide">
                                    {!l.tuteur ? 'Aucun tuteur affecté.' : `Fiche pas encore remplie par ${l.tuteur}`}
                                    {l.echeance_visite && <> — visite attendue avant le {dateFr(l.echeance_visite)}.</>}
                                </div>
                            ) : (
                                <>
                                    <div className="eval-volet-meta">
                                        Modèle « {v.modele_nom} » (figé pour la campagne){v.tuteur ? ` · ${v.tuteur}` : ''} · visite du {dateFr(v.date_visite)}
                                        {' · '}<i className={'fa-solid ' + (ICONE_MODE[v.mode_visite] || 'fa-circle')} /> {v.mode_libelle}
                                        {v.duree_minutes ? ` · ${duree(v.duree_minutes)}` : ''}
                                        {' · '}enregistrée le {dateFr(v.date_soumission)} à {heureFr(v.date_soumission)}
                                        {v.remplie_par && v.remplie_par !== v.tuteur ? ` par ${v.remplie_par}` : ''}
                                        {v.porte === 'compte' ? ' (Mes visites)' : ''}
                                    </div>
                                    {v.criteres.map((c, i) => <LigneCritere key={i} c={c} />)}
                                    {v.commentaire && <div className="ev-com ev-com-general"><b>Commentaire du tuteur :</b> « {v.commentaire} »</div>}
                                    {v.nb_precedentes > 0 && (
                                        <div className="eval-volet-meta" style={{ marginTop: 6 }}>
                                            <i className="fa-solid fa-clock-rotate-left" /> Fiche modifiée {v.nb_precedentes === 1 ? 'une fois' : `${v.nb_precedentes} fois`}
                                            {' '}(version du {dateFr(v.derniere_precedente)} à {heureFr(v.derniere_precedente)} conservée{v.nb_precedentes > 1 ? ', et les précédentes' : ''})
                                        </div>
                                    )}
                                </>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Lot L1a (E169) : `configurationId` = la vignette ouverte — le filtre « Processus » est fixé et ne s'affiche pas.
function StageEvaluations({ config, user, anneeId, onParametrage, onOuvrirDossier, voletInitial = null, configurationId = null }) {
    const departementId = departementCourant(user, config);
    const [donnees, setDonnees] = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [recherche, setRecherche] = useState('');
    const [processus, setProcessus] = useState(configurationId ? String(configurationId) : '');
    const [volet, setVolet] = useState(voletInitial);
    const [exportOuvert, setExportOuvert] = useState(false);
    const [choixExport, setChoixExport] = useState(() => {
        try { return sessionStorage.getItem(CLE_EXPORT) === 'detail' ? 'detail' : 'final'; } catch (_) { return 'final'; }
    });

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const recharger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true); setErreur(null);
        try {
            const r = await stageAPI.getEvaluations(departementId, anneeId);
            setDonnees(r.data);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message || 'Lecture des évaluations impossible');
            setDonnees(null);
        } finally { setChargement(false); }
    }, [departementId, anneeId]);
    useEffect(() => { recharger(); }, [recharger]);

    const toutes = useMemo(() => donnees?.lignes || [], [donnees]);
    const listeProcessus = useMemo(() => [...new Map(toutes.map((l) => [l.configuration_id, l.processus])).entries()], [toutes]);
    const lignes = useMemo(() => {
        const q = normaliser(recherche.trim());
        return toutes.filter((l) => (!processus || String(l.configuration_id) === processus)
            && (!q || normaliser(`${l.nom} ${l.prenom}`).includes(q) || normaliser(`${l.prenom} ${l.nom}`).includes(q)));
    }, [toutes, processus, recherche]);

    const recues = lignes.filter((l) => l.resultat).length;
    const visites = lignes.filter((l) => l.visite).length;
    // E121 : « / X pts » dans l'en-tête seulement si TOUTES les notes visibles ont le même barème.
    const baremes = [...new Set(lignes.filter((l) => l.resultat).map((l) => l.resultat.total_max))];
    const baremeCommun = baremes.length === 1 ? baremes[0] : null;
    const ouvert = volet ? toutes.find((l) => l.id === volet) : null;
    const fermerVolet = useCallback(() => setVolet(null), []);

    const exporter = () => {
        try { sessionStorage.setItem(CLE_EXPORT, choixExport); } catch (_) { /* mémoire indisponible : sans conséquence */ }
        const annee = String(donnees?.annee?.libelle || '').replace(/[^0-9-]/g, '');
        const jourIso = new Date().toISOString().slice(0, 10);
        telechargerCsv(`evaluations_${choixExport === 'detail' ? 'detail' : 'notes'}_${annee}_${jourIso}.csv`,
            choixExport === 'detail' ? csvDetail(lignes) : csvNotes(lignes));
        setExportOuvert(false);
    };

    return (
        <div>
            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Rechercher un étudiant…" value={recherche} aria-label="Rechercher un étudiant"
                           onChange={(e) => setRecherche(e.target.value)} />
                </div>
                {!configurationId && (
                <select className="input-sm stg-filtre" value={processus} aria-label="Processus" title="Un processus = une formation × un semestre"
                        onChange={(e) => setProcessus(e.target.value)}>
                    <option value="">Tous les processus</option>
                    {listeProcessus.map(([id, lib]) => <option key={id} value={String(id)}>{lib}</option>)}
                </select>
                )}
                <div style={{ marginLeft: 'auto' }}>
                    <button type="button" className="btn btn-outline btn-sm" disabled={!lignes.length}
                            title={lignes.length ? 'Exporter les lignes affichées' : 'Aucune ligne à exporter'}
                            onClick={() => setExportOuvert(true)}>
                        <i className="fa-solid fa-download" /> Exporter
                    </button>
                </div>
            </div>

            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="card">
                <div className="card-header">
                    <span>
                        <i className="fa-solid fa-star-half-stroke" style={{ color: 'var(--stg-vert-d)' }} /> Résultats d'évaluation
                        {!chargement && <span style={{ fontWeight: 400, color: 'var(--stg-muted)' }}>
                            {' '}· {lignes.length} étudiant{lignes.length > 1 ? 's' : ''} · {recues} grille{recues > 1 ? 's' : ''} reçue{recues > 1 ? 's' : ''}
                            {' '}· {visites} visite{visites > 1 ? 's' : ''}{donnees?.annee?.libelle ? ` · ${donnees.annee.libelle}` : ''}
                        </span>}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--stg-muted)', fontWeight: 400 }}>
                        Cliquez une ligne : le détail des notes s'ouvre dans le volet de droite · grilles configurées dans{' '}
                        {onParametrage
                            ? <button type="button" className="stg-lien" onClick={onParametrage}>Paramétrage</button>
                            : 'Paramétrage'}
                    </span>
                </div>
                <div className="stg-table-defile">
                    <table>
                        <thead>
                            <tr>
                                <th>Étudiant</th>
                                <th>Processus</th>
                                <th>Tuteur pédagogique</th>
                                <th>Évaluation maître de stage{baremeCommun !== null && <span style={{ fontWeight: 400, color: 'var(--stg-muted)' }}> / {nombre(baremeCommun)} pts</span>}</th>
                                <th>Visite tuteur</th>
                                <th style={{ width: 32 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {chargement && !donnees && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--stg-muted)' }}><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture…</td></tr>}
                            {!chargement && !lignes.length && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--stg-muted)' }}>Aucun étudiant ne correspond.</td></tr>}
                            {lignes.map((l) => {
                                const quelqueChose = !!(l.resultat || l.visite);
                                return (
                                    <tr key={l.id} className={'eval-row' + (quelqueChose ? '' : ' attente')}
                                        onClick={() => { if (quelqueChose) setVolet(l.id); }}>
                                        <td><strong>{l.nom} {l.prenom}</strong></td>
                                        <td><span style={{ fontSize: 11, color: 'var(--stg-muted)' }}>{l.processus}</span></td>
                                        <td>{!l.visite_active
                                            ? <span className="eval-attente">Sans objet (pas de visite)</span>
                                            : (l.tuteur || <span className="eval-a-faire">Non affecté</span>)}</td>
                                        <td><CelluleNote ligne={l} /></td>
                                        <td><CelluleVisite ligne={l} /></td>
                                        <td>
                                            {quelqueChose && (
                                                <button type="button" className="eval-chevron-btn"
                                                        aria-label={`Ouvrir le détail de ${l.nom} ${l.prenom}`}
                                                        onClick={(e) => { e.stopPropagation(); setVolet(l.id); }}>
                                                    <i className="fa-solid fa-chevron-right eval-chevron" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {ouvert && <VoletEvaluation l={ouvert} onFermer={fermerVolet}
                                        onDossier={onOuvrirDossier ? (l) => { setVolet(null); onOuvrirDossier(l); } : null} />}

            {exportOuvert && (
                <StageModale titre="Exporter les évaluations" icone="fa-download" libelleValider="Télécharger CSV"
                             onValider={exporter} onFermer={() => setExportOuvert(false)}>
                    <div style={{ fontSize: 12, color: 'var(--stg-muted)', marginBottom: 12 }}>
                        Choisissez le contenu à exporter — les <b>{lignes.length}</b> ligne{lignes.length > 1 ? 's' : ''} affichée{lignes.length > 1 ? 's' : ''} :
                    </div>
                    <label className="eval-export-choix">
                        <input type="radio" name="eval-export" value="final" checked={choixExport === 'final'} onChange={() => setChoixExport('final')} />
                        <div>
                            <div className="eval-export-choix-titre">Notes finales uniquement</div>
                            <div className="eval-export-choix-aide">1 ligne par étudiant · Nom, Prénom, N° étudiant, Formation, Semestre, Tuteur pédagogique, la note du maître sous le nom de la grille, Date de réception, puis la visite du tuteur (note, date, mode)</div>
                        </div>
                    </label>
                    <label className="eval-export-choix">
                        <input type="radio" name="eval-export" value="detail" checked={choixExport === 'detail'} onChange={() => setChoixExport('detail')} />
                        <div>
                            <div className="eval-export-choix-titre">Détail par critère</div>
                            <div className="eval-export-choix-aide">1 ligne par critère des grilles reçues ({recues}) et des fiches de visite ({visites}) · niveau, points, commentaire</div>
                        </div>
                    </label>
                </StageModale>
            )}
        </div>
    );
}

export default StageEvaluations;
